// "Matches played" (2026-11-01, Devin: "build the number of matches played just like the sheep tag"). The rule is
// Sheep Tag's (its server/played.ts), so the number stays honest when anyone can POST:
// - An online match is counted by its room, once, when the room ends (worker/rooms.ts). Clients never count one.
// - A match against bots happens only in the browser, so the game reports it (src/client/main.ts): once a match, when
//   it is decided for you or you leave it, and only after a minute of it was played. A client adds at most
//   LOCAL_PER_DAY a day; the key is its address (an IPv6 one by its /64), held in memory for the day and never stored.
// - "N playing online now" is the people in rooms that reported in the last ONLINE_FRESH_MS.
// Pure, so tests hold it without Durable Objects; the Matchmaker object keeps one Tally and stores its total and rooms.

/** A match counts once this many ticks of it were played (a minute at 20 Hz): a click on Play and straight out is not one. */
export const PLAYED_MIN_TICKS = 1200;
/** Matches one client may add in a day. A match takes minutes, so 40 is more than a person plays. */
export const LOCAL_PER_DAY = 40;
/** A room reports its people when they change and at least this often; one silent for longer has gone. */
export const ROOM_REPORT_MS = 60_000;
const ONLINE_FRESH_MS = ROOM_REPORT_MS * 2.5;

/** Whether this page should report its match now. `counted` is also set for an online match from its start: the room
 *  counts that one, even if it closes and bots finish it here. Watching is not playing. */
export function countsLocal(m: { counted: boolean; me: number; ticks: number; staged: boolean }) {
  return !m.counted && m.me >= 0 && !m.staged && m.ticks >= PLAYED_MIN_TICKS;
}

/** The address a client's local allowance is kept under: an IPv4 address whole, an IPv6 one by its /64. */
export function clientKey(ip: string | null): string {
  if (!ip) return '?';
  if (!ip.includes(':')) return ip;
  const full = ip.includes('::') ? expand(ip) : ip.split(':');
  return full.slice(0, 4).map((x) => x.toLowerCase().replace(/^0+(?=.)/, '')).join(':');
}
function expand(ip: string) {
  const [a, b] = ip.split('::'), head = a ? a.split(':') : [], tail = b ? b.split(':') : [];
  return [...head, ...Array(Math.max(0, 8 - head.length - tail.length)).fill('0'), ...tail];
}

export class Tally {
  total = 0;
  private day = '';
  private perKey = new Map<string, number>();
  /** Each online room's people and when it last reported. Kept in storage by the Matchmaker, not just here: a Durable
   *  Object left idle between reports is evicted and its memory with it, and a fresh one read 0 online (E2E, 2026-11-01). */
  rooms: Record<string, { people: number; at: number }> = {};

  /** A match against bots, reported by a client. True when it counted. */
  local(key: string, now: number): boolean {
    const day = new Date(now).toISOString().slice(0, 10);
    if (day !== this.day) { this.day = day; this.perKey.clear(); }
    const n = this.perKey.get(key) ?? 0;
    if (n >= LOCAL_PER_DAY) return false;
    this.perKey.set(key, n + 1);
    this.total++;
    return true;
  }

  /** A room's report: its people now, and `played` once, as it ends, if its match ran long enough. */
  room(code: string, people: number, played: boolean, now: number) {
    if (played) this.total++;
    if (people > 0) this.rooms[code] = { people, at: now }; else delete this.rooms[code];
  }

  /** People in online rooms now. */
  online(now: number): number {
    let n = 0;
    for (const [code, r] of Object.entries(this.rooms)) { if (now - r.at > ONLINE_FRESH_MS) delete this.rooms[code]; else n += r.people; }
    return n;
  }
}
